import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

const ADDRESS_TYPES = ["house", "street", "district", "locality", "city"];
const AREA_TYPES = ["county", "state", "country", "other"];

function mapsKey() {
  const runtime = window.ScreenworkMapsRuntime || {};
  return typeof runtime.geocoderKey === "string" ? runtime.geocoderKey.trim() : "";
}

function isValidLatLon(lat, lon) {
  return typeof lat === "number" && isFinite(lat) && lat >= -90 && lat <= 90
    && typeof lon === "number" && isFinite(lon) && lon >= -180 && lon <= 180;
}

function text(value) {
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }

  if (typeof value === "string" && value.trim() !== "") {
    return value.trim();
  }

  return null;
}

function features(res) {
  if (res && res.data && Array.isArray(res.data.features)) {
    return res.data.features;
  }

  return [];
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.reverseRequest = null;
    this.autocompleteSeq = 0;
    this.reverseSeq = 0;
  }

  cancelAutocomplete() {
    this.autocompleteSeq++;

    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  cancelReverse() {
    this.reverseSeq++;

    if (this.reverseRequest != null) {
      this.reverseRequest.source.cancel();
      this.reverseRequest = null;
    }
  }

  cancel() {
    this.cancelAutocomplete();
    this.cancelReverse();
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    // GeoJSON: [lng, lat]
    const lng = l.geometry.coordinates[0];
    const lat = l.geometry.coordinates[1];
    if (!isValidLatLon(lat, lng)) {
      return null;
    }

    const p = l.properties;
    const name = text(p.name);
    const street = text(p.street);
    const housenumber = text(p.housenumber);
    const postcode = text(p.postcode);
    const city = text(p.city);
    const district = text(p.district);

    const g = {
      street: null,
      number: null,
      zip: postcode,
      city: null,
      district: null
    };
    let label = null;

    if (ADDRESS_TYPES.indexOf(p.type) !== -1) {
      switch (p.type) {
        case "house":
          g.street = street;
          g.number = street ? housenumber : null;
          g.city = city;
          g.district = district;
          break;
        case "street":
          g.street = street || name;
          g.city = city;
          g.district = district;
          break;
        case "district":
        case "locality":
          g.city = city;
          g.district = name || district;
          break;
        case "city":
          g.city = name || city;
          break;
      }
    } else if (AREA_TYPES.indexOf(p.type) !== -1) {
      g.city = city;
      label = name;
    } else {
      // Fallback für Antworten ohne bekannten Typ
      const highway = p.osm_key === "highway";
      const placeName = highway ? null : name;

      g.street = street || (highway ? name : null);
      g.number = g.street ? housenumber : null;

      if (city) {
        g.city = city;
        const isDistrict = p.osm_key === "boundary" || (p.osm_key === "place" && p.osm_value === "suburb");
        if (placeName && placeName !== city && isDistrict) {
          g.district = placeName;
        } else if (district) {
          g.district = district;
        }
      } else if (placeName && p.osm_key === "place") {
        g.city = placeName;
      } else {
        label = placeName;
      }
    }

    if (g.district && g.district === g.city) {
      g.district = null;
    }

    const geoaddress = {};
    ["street", "number", "zip", "city", "district"].forEach(k => {
      if (g[k]) {
        geoaddress[k] = g[k];
      }
    });

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;

    geoaddress["state"] = text(p.state) || undefined;
    geoaddress["country"] = text(p.country) || undefined;

    let t = this._geoaddressText(geoaddress);
    if (label && t.indexOf(label) === -1) {
      t = t ? label + ", " + t : label;
    }

    if (!t) {
      return null;
    }

    geoaddress["text"] = t;

    return geoaddress;
  }

  _results(res) {
    const results = [];

    features(res).forEach(l => {
      const geoaddress = this._geoaddress(l);
      if (geoaddress) {
        results.push(geoaddress);
      }
    });

    return results;
  }

  // Liefert die Treffer, [] ohne Key oder Suchtext und null, wenn die Anfrage
  // durch eine neuere Suche oder einen Abbruch überholt wurde.
  autocomplete(q) {
    this.cancelAutocomplete();

    const seq = this.autocompleteSeq;
    const key = mapsKey();
    const query = typeof q === "string" ? q.trim() : "";

    if (!key || !query) {
      return Promise.resolve([]);
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (seq !== this.autocompleteSeq) {
        return null;
      }

      return this._results(res);
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.autocompleteSeq) {
        return null;
      }

      throw thrown;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  // Liefert die Adresse oder null, wenn die Anfrage überholt wurde.
  reverse(lat, lng) {
    this.cancelReverse();

    const seq = this.reverseSeq;
    const key = mapsKey();
    const la = parseFloat(lat);
    const lo = parseFloat(lng);

    if (!key) {
      return Promise.reject(new Error("geocoder unavailable"));
    }

    if (!isValidLatLon(la, lo)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const request = {
      source: this.axios.CancelToken.source()
    };
    this.reverseRequest = request;

    const params = new URLSearchParams();
    params.append("lat", String(la));
    params.append("lon", String(lo));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (seq !== this.reverseSeq) {
        return null;
      }

      const results = this._results(res);
      if (results.length === 0) {
        throw new Error("invalid data");
      }

      return results[0];
    }).catch(thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.reverseSeq) {
        return null;
      }

      throw thrown;
    }).finally(() => {
      if (this.reverseRequest === request) {
        this.reverseRequest = null;
      }
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
